import React, { useState } from 'react';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';

export interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  required?: boolean;
}

interface ColumnVisibilityMenuProps {
  columns: ColumnConfig[];
  onColumnToggle: (columnId: string) => void;
  onResetDefaults?: () => void;
}

const ColumnVisibilityMenu: React.FC<ColumnVisibilityMenuProps> = ({
  columns,
  onColumnToggle,
  onResetDefaults,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonRef, setButtonRef] = useState<HTMLButtonElement | null>(null);

  const visibleCount = columns.filter(col => col.visible).length;

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={setButtonRef}
        onClick={() => setIsOpen(!isOpen)}
        title="Customize columns"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
          padding: '7px 13px',
          borderRadius: '8px',
          fontFamily: 'inherit',
          fontSize: '12.5px',
          fontWeight: 500,
          cursor: 'pointer',
          border: '1px solid #E2E8F0',
          background: '#FFFFFF',
          color: '#475569',
          boxShadow: '0 1px 2px rgba(0,0,0,.05)',
          transition: 'all 0.15s',
          whiteSpace: 'nowrap',
          lineHeight: 1,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#F1F5F9';
          e.currentTarget.style.color = '#0F172A';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#FFFFFF';
          e.currentTarget.style.color = '#475569';
        }}
      >
        <ViewColumnIcon style={{ fontSize: 16, width: '13px', height: '13px', flexShrink: 0 }} />
        Columns ({visibleCount}/{columns.length})
      </button>

      {isOpen && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
            }}
            onClick={() => setIsOpen(false)}
          />
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              right: 0,
              background: '#FFFFFF',
              border: '1px solid #E2E8F0',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              minWidth: '280px',
              maxHeight: '500px',
              overflowY: 'auto',
              zIndex: 1000,
            }}
          >
            <div style={{ padding: '16px' }}>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontWeight: 600, color: '#1E293B', fontSize: '14px', marginBottom: '4px' }}>
                  Show/Hide Columns
                </div>
                <div style={{ color: '#64748B', fontSize: '12px' }}>
                  Select which columns to display in the table
                </div>
              </div>

              <div style={{ borderTop: '1px solid #E2E8F0', marginBottom: '12px' }} />

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {columns.map((column) => (
                  <label
                    key={column.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: column.required ? 'not-allowed' : 'pointer',
                      opacity: column.required ? 0.6 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={column.visible}
                      onChange={() => !column.required && onColumnToggle(column.id)}
                      disabled={column.required}
                      style={{
                        width: '16px',
                        height: '16px',
                        cursor: column.required ? 'not-allowed' : 'pointer',
                        accentColor: '#2563EB',
                      }}
                    />
                    <span style={{ fontSize: '13px', color: '#475569' }}>
                      {column.label}
                      {column.required && (
                        <span style={{ fontSize: '11px', color: '#94A3B8', marginLeft: '6px' }}>
                          (Required)
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>

              {onResetDefaults && (
                <>
                  <div style={{ borderTop: '1px solid #E2E8F0', margin: '12px 0' }} />
                  <button
                    onClick={() => {
                      onResetDefaults();
                      setIsOpen(false);
                    }}
                    style={{
                      width: '100%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '5px',
                      padding: '7px 13px',
                      borderRadius: '8px',
                      fontFamily: 'inherit',
                      fontSize: '12.5px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      border: '1px solid #E2E8F0',
                      background: '#FFFFFF',
                      color: '#475569',
                      boxShadow: '0 1px 2px rgba(0,0,0,.05)',
                      transition: 'all 0.15s',
                      whiteSpace: 'nowrap',
                      lineHeight: 1,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#F1F5F9';
                      e.currentTarget.style.color = '#0F172A';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#FFFFFF';
                      e.currentTarget.style.color = '#475569';
                    }}
                  >
                    Reset to Defaults
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ColumnVisibilityMenu;
